/**
 * @internal
 */
export interface State {
    x: number
    v: number
}

/**
 * @internal
 */
export type AccelerationFunction = (state: State) => number

/**
 * @internal
 */
interface Delta {
    dx: number
    dv: number
}

/**
 * @internal
 */
export class Integrator {
    private accelerationForState: AccelerationFunction

    public constructor(accelerationFunction: AccelerationFunction) {
        this.accelerationForState = accelerationFunction
    }

    public integrateState(state: State, dt: number) {
        const a = this.evaluateState(state)
        const b = this.evaluateStateWithDerivative(state, dt * 0.5, a)
        const c = this.evaluateStateWithDerivative(state, dt * 0.5, b)
        const d = this.evaluateStateWithDerivative(state, dt, c)

        const dxdt = (1.0 / 6.0) * (a.dx + 2.0 * (b.dx + c.dx) + d.dx)
        const dvdt = (1.0 / 6.0) * (a.dv + 2.0 * (b.dv + c.dv) + d.dv)

        state.x = state.x + dxdt * dt
        state.v = state.v + dvdt * dt

        return state
    }

    private evaluateState(initialState: State): Delta {
        const dv = this.accelerationForState(initialState)
        return { dx: initialState.v, dv: dv }
    }

    private evaluateStateWithDerivative(initialState: State, dt: number, derivative: Delta): Delta {
        const state = {
            x: initialState.x + derivative.dx * dt,
            v: initialState.v + derivative.dv * dt,
        }
        const output = {
            dx: state.v,
            dv: this.accelerationForState(state),
        }
        return output
    }
}
